/**
 * Debounce + throttle. Replaces ad-hoc implementations scattered across
 * global-utils.js and several modal files.
 */

/**
 * Trailing-edge debounce. The wrapped function fires once after `wait` ms of
 * silence. Repeated calls within the window reset the timer.
 *
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} wait
 * @returns {(...args: Parameters<F>) => void}
 */
export function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
}

/**
 * Leading-edge throttle. The first call fires immediately; subsequent calls
 * within the `limit` window are ignored. After the window, the next call fires
 * again. No trailing call is queued.
 *
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} limit
 * @returns {(...args: Parameters<F>) => void}
 */
export function throttle(fn, limit) {
  let blocked = false;
  return (...args) => {
    if (blocked) return;
    blocked = true;
    try { fn(...args); } finally {
      setTimeout(() => { blocked = false; }, limit);
    }
  };
}
